import { BellIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { localized, useNotifications } from "../../context/NotificationsContext";
import { fetchNotifications } from "../../services/notificationService";

const PER_PAGE = 10;

/** "3 minutes ago" in the UI language, from an ISO instant. */
const relativeTime = (iso, language) => {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  const steps = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, "second");
};

/**
 * The top-bar bell: unread badge and a dropdown of the user's notifications
 * from the platform. Clicking an item only marks it read — no navigation.
 */
export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const { unread, permission, listVersion, panelRequest, markRead, markAllRead, enablePush, openTarget } =
    useNotifications();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const wrapper = useRef(null);

  const load = useCallback(async (nextPage) => {
    setLoading(true);
    setFailed(false);
    try {
      const { notifications, meta } = await fetchNotifications({ page: nextPage, perPage: PER_PAGE });
      setItems((current) => (nextPage === 1 ? notifications : [...current, ...notifications]));
      setPage(meta.current_page);
      setLastPage(meta.last_page);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fresh list every time the panel opens, and when a push lands while open.
  useEffect(() => {
    if (open) load(1);
  }, [open, listVersion, load]);

  // Opened from elsewhere, e.g. clicking a popup that has no link of its own.
  useEffect(() => {
    if (panelRequest) setOpen(true);
  }, [panelRequest]);

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A stored event opens its target (marking it read on the way); anything
  // else — a broadcast, an unknown type — is only marked read, as before.
  const readOne = (item) => {
    if (!item.is_read) {
      setItems((current) => current.map((i) => (i.id === item.id ? { ...i, is_read: true } : i)));
    }

    if (item.target) {
      setOpen(false);
      openTarget({ id: item.is_read ? null : item.id, target: item.target, branchId: item.branch_id });
      return;
    }

    if (!item.is_read) markRead(item.id);
  };

  const readAll = () => {
    setItems((current) => current.map((i) => ({ ...i, is_read: true })));
    markAllRead();
  };

  const badge = unread > 9 ? "9+" : String(unread);

  return (
    <div className="relative" ref={wrapper}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("notifications.bell")}
        aria-expanded={open}
        title={t("notifications.bell")}
        className="relative flex min-h-touch items-center rounded-control p-2 text-ink transition-colors duration-fast hover:bg-raised"
      >
        <BellIcon className="h-6 w-6" />
        {unread > 0 && (
          <span className="absolute -top-0.5 end-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none text-danger-on">
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("notifications.title")}
          className="absolute end-0 top-full z-40 mt-2 flex max-h-[70vh] w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-card border border-line bg-surface shadow-e2"
        >
          <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
            <p className="text-card-title font-semibold text-ink">{t("notifications.title")}</p>
            <button
              type="button"
              onClick={readAll}
              disabled={unread === 0}
              className="text-xs font-semibold text-accent-text hover:underline disabled:cursor-default disabled:text-ink-muted disabled:no-underline"
            >
              {t("notifications.markAll")}
            </button>
          </div>

          {permission === "default" && (
            <div className="flex items-center justify-between gap-2 border-b border-line bg-info-wash px-4 py-2 text-xs text-info-text">
              <span>{t("notifications.enableHint")}</span>
              <button type="button" onClick={enablePush} className="shrink-0 font-semibold underline">
                {t("notifications.enable")}
              </button>
            </div>
          )}
          {permission === "denied" && (
            <div className="border-b border-line bg-raised px-4 py-2 text-xs text-ink-2">
              {t("notifications.blockedHint")}
            </div>
          )}

          <ul className="flex-1 overflow-y-auto">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => readOne(item)}
                  className={`flex w-full items-start gap-3 border-b border-line px-4 py-3 text-start transition-colors duration-fast hover:bg-raised ${item.is_read ? "" : "bg-accent-wash"}`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${item.is_read ? "bg-transparent" : "bg-accent"}`}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block break-words text-sm text-ink ${item.is_read ? "" : "font-semibold"}`}
                    >
                      {localized(item.translations?.title, i18n.language) || item.title}
                    </span>
                    <span className="mt-0.5 block whitespace-pre-line break-words text-xs text-ink-2">
                      {localized(item.translations?.body, i18n.language) || item.body}
                    </span>
                    <span className="mt-1 block text-xs text-ink-2">
                      {relativeTime(item.created_at, i18n.language)}
                      {!item.is_read && <span className="sr-only"> · {t("notifications.unread")}</span>}
                    </span>
                  </span>
                </button>
              </li>
            ))}

            {!loading && !failed && items.length === 0 && (
              <li className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-ink-2">
                <BellIcon className="h-8 w-8 text-ink-muted" />
                {t("notifications.empty")}
              </li>
            )}
            {failed && (
              <li className="px-4 py-6 text-center text-sm text-danger-text">
                {t("notifications.loadFailed")}{" "}
                <button type="button" className="underline" onClick={() => load(1)}>
                  {t("notifications.retry")}
                </button>
              </li>
            )}
          </ul>

          {(loading || page < lastPage) && (
            <div className="border-t border-line px-4 py-2 text-center">
              {loading ? (
                <span className="text-xs text-ink-2">{t("notifications.loading")}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => load(page + 1)}
                  className="text-xs font-semibold text-accent-text hover:underline"
                >
                  {t("notifications.loadMore")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
